import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, articleCategories, getArticlesByCategory } from '@/mocks/articlesData';
import type { Article } from '@/mocks/articlesData';

const articleImages: Record<string, string> = {
  'art-001': 'https://readdy.ai/api/search-image?query=A%20warm%20intimate%20scene%20of%20a%20couple%20sharing%20a%20quiet%20evening%20moment%20together%20on%20a%20comfortable%20sofa%2C%20soft%20warm%20lighting%20from%20a%20table%20lamp%2C%20cozy%20living%20room%20atmosphere%2C%20emotional%20connection%2C%20gentle%20and%20tender%20mood%2C%20editorial%20lifestyle%20photography%20with%20soft%20focus&width=800&height=500&seq=art-thumb-001&orientation=landscape',
  'art-002': 'https://readdy.ai/api/search-image?query=An%20elegant%20minimalist%20living%20space%20with%20warm%20morning%20sunlight%20streaming%20through%20large%20windows%2C%20a%20beautifully%20set%20breakfast%20table%20with%20fresh%20flowers%20and%20ceramic%20tableware%2C%20quality%20lifestyle%20aesthetic%2C%20clean%20and%20serene%20atmosphere%2C%20soft%20cream%20and%20beige%20tones%2C%20editorial%20interior%20photography&width=800&height=500&seq=art-thumb-002&orientation=landscape',
  'art-003': 'https://readdy.ai/api/search-image?query=A%20serene%20person%20sitting%20alone%20by%20a%20window%20with%20warm%20afternoon%20light%2C%20holding%20a%20cup%20of%20tea%2C%20peaceful%20and%20contemplative%20expression%2C%20soft%20natural%20lighting%2C%20minimalist%20interior%20with%20a%20small%20plant%2C%20calm%20and%20introspective%20mood%2C%20fine%20art%20portrait%20photography&width=800&height=500&seq=art-thumb-003&orientation=landscape',
  'art-004': 'https://readdy.ai/api/search-image?query=Two%20people%20having%20a%20warm%20heartfelt%20conversation%20in%20a%20cozy%20cafe%2C%20soft%20window%20light%2C%20intimate%20atmosphere%2C%20genuine%20emotional%20connection%2C%20warm%20tones%20of%20cream%20and%20gold%2C%20lifestyle%20photography%20with%20natural%20expressions&width=800&height=500&seq=art-thumb-004&orientation=landscape',
  'art-005': 'https://readdy.ai/api/search-image?query=A%20peaceful%20meditation%20space%20with%20morning%20sunlight%2C%20a%20simple%20cushion%20on%20wooden%20floor%2C%20a%20small%20candle%20and%20dried%20flowers%2C%20minimalist%20zen%20atmosphere%2C%20soft%20natural%20lighting%2C%20serene%20and%20calm%20mood%2C%20fine%20art%20interior%20photography&width=800&height=500&seq=art-thumb-005&orientation=landscape',
  'art-006': 'https://readdy.ai/api/search-image?query=A%20cozy%20warm%20living%20room%20interior%20with%20soft%20throw%20blankets%2C%20flickering%20candles%2C%20indoor%20plants%2C%20warm%20ambient%20lighting%2C%20books%20on%20wooden%20shelves%2C%20inviting%20and%20comfortable%20atmosphere%2C%20home%20decor%20lifestyle%20photography&width=800&height=500&seq=art-thumb-006&orientation=landscape',
  'art-007': 'https://readdy.ai/api/search-image?query=An%20elderly%20couple%20walking%20hand%20in%20hand%20on%20a%20tree%20lined%20path%20during%20golden%20hour%2C%20warm%20sunset%20light%2C%20timeless%20love%20and%20commitment%2C%20emotional%20and%20heartwarming%20scene%2C%20romantic%20fine%20art%20photography&width=800&height=500&seq=art-thumb-007&orientation=landscape',
  'art-008': 'https://readdy.ai/api/search-image?query=A%20person%20journaling%20by%20a%20window%20with%20soft%20natural%20light%2C%20a%20cup%20of%20tea%20nearby%2C%20peaceful%20self%20reflection%20moment%2C%20minimalist%20desk%20setup%2C%20calm%20and%20mindful%20atmosphere%2C%20emotional%20wellness%20photography&width=800&height=500&seq=art-thumb-008&orientation=landscape',
};

export default function ArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCategory = searchParams.get('category') || 'all';
  const [displayedArticles, setDisplayedArticles] = useState<Article[]>([]);
  const [animKey, setAnimKey] = useState(0);

  const categoryLabel = useMemo(() => {
    const cat = articleCategories.find((c) => c.key === currentCategory);
    return cat ? cat.label : '全部文章';
  }, [currentCategory]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setAnimKey((k) => k + 1);
    const filtered = getArticlesByCategory(currentCategory);
    setDisplayedArticles(filtered);
  }, [currentCategory]);

  const handleCategoryChange = (category: string) => {
    if (category === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '精选文章' },
            ]}
          />
        </div>

        <section className="w-full py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                精选文章
              </h1>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                探索情感陪伴、品质生活、心灵成长等多元内容，找到属于你的温暖共鸣
              </p>
            </div>

            <div className="flex justify-center mb-10">
              <div className="inline-flex items-center bg-background-100 rounded-full px-1 py-1 flex-wrap gap-1 justify-center">
                {articleCategories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => handleCategoryChange(cat.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${
                      currentCategory === cat.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div key={animKey} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto animate-fadeIn">
              {displayedArticles.map((article: Article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={articleImages[article.id] || articleImages['art-001']}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                        {article.categoryLabel}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between gap-3 text-xs text-foreground-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {article.publishedAt}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          {article.readTime}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-primary-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                        阅读全文
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {displayedArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}